// ============================================================================
// /apply/:token — PUBLIC partner application (bilingual progressive intake).
// No auth shell — mirrors /onboard/:token. Phone-first (380px).
//
// Flow: token check (sourcing_intake_context, anon RPC) → intro → one tranche
// at a time → every applicant answers every question → finish writes an
// attributed Lead via submit_sourcing_intake (open link) or fills the sub's own
// application (submit_sourcing_intake_for_prospect).
// ⛔ NO EARLY EXIT (TCSD-0493, Ben, three times). Answers that count against an
// applicant are tagged for the account manager on the profile; they end nothing.
//
// Terminology rule: this page addresses a prospective PARTNER — never
// "subcontractor"/"sub". Questions are DRAFT placeholders (questions.ts,
// marked "PLACEHOLDER — Jack to replace"); swapping copy touches no engine
// code.
// ============================================================================

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { CheckCircle2, Globe, Loader2 } from 'lucide-react';
import { supabase } from '../../shared/supabase';
import {
  extractContact,
  extractSignals,
  marketsFromContext,
  prefillAnswers,
  trancheProblems,
  withMarketOptions,
  type AnswerProblem,
  type Answers,
  type MarketChoice,
} from './engine';
import { INTAKE_STRINGS, INTAKE_TRANCHES, type IntakeQuestion } from './questions';
import { scrollPageTop } from '../../shared/public-page';

type Lang = 'en' | 'es';
type Phase = 'loading' | 'invalid' | 'intro' | 'form' | 'submitting' | 'done';

// ⛔ SHELL IS DECLARED AT MODULE LEVEL AND MUST STAY THERE.
// It used to be declared INSIDE SourcingIntakePage, which makes it a NEW
// component type on every render. React compares types by identity, so every
// keystroke unmounted the whole form and mounted a fresh copy: the input lost
// focus after a single character, every time, on every text field. The choice
// questions were unaffected because a button click needs no sustained focus,
// which is why the middle step looked fine and the first and last did not.
// ⛔ Never declare a component inside another component.
function Shell({
  t,
  onToggleLang,
  children,
}: {
  t: (typeof INTAKE_STRINGS)[Lang];
  onToggleLang: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="min-h-full bg-tcs-bg-muted px-4 py-6">
      <div className="mx-auto w-full max-w-md">
        <div className="mb-4 flex items-start justify-between gap-3">
          <div>
            <h1 className="text-xl font-bold text-tcs-text">{t.title}</h1>
            <p className="text-xs text-tcs-text-muted">{t.subtitle}</p>
          </div>
          <button
            type="button"
            onClick={onToggleLang}
            className="inline-flex min-h-11 items-center gap-1.5 rounded-lg border border-tcs-border-strong bg-tcs-raised px-3 text-sm font-medium text-tcs-text-secondary"
          >
            <Globe className="h-4 w-4" /> {t.lang_toggle}
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

export function SourcingIntakePage() {
  const { token } = useParams<{ token: string }>();
  const [lang, setLang] = useState<Lang>('en');
  const [phase, setPhase] = useState<Phase>('loading');
  const [trancheIdx, setTrancheIdx] = useState(0);
  const [answers, setAnswers] = useState<Answers>({});
  const [err, setErr] = useState<string | null>(null);
  // ⛔ WHICH LINK THIS IS DECIDES WHICH DOOR THE SUBMIT GOES THROUGH.
  // A per-prospect token answers `kind: 'prospect'`; an open flyer code answers
  // its own kind. They are TWO functions in the database and they do different
  // things — one updates the prospect the link was minted for and burns the
  // token, the other validates a source code and creates a new Lead. The page
  // used to call the open-code one for both, so a tokenized link could never be
  // submitted at all: the code lookup found nothing and the applicant was told
  // the link was invalid, on the last screen, after answering every question.
  const [isProspectLink, setIsProspectLink] = useState(false);
  // What is wrong with this step's answers, keyed by question. Shown UNDER the
  // field it belongs to, because a single line at the bottom saying "please
  // answer to continue" does not say which box.
  const [problems, setProblems] = useState<Record<string, AnswerProblem>>({});
  // The cities for "Which area do you work in?" — the master market list, as
  // the link check returned it (item 1052). Never a typed list.
  const [markets, setMarkets] = useState<MarketChoice[]>([]);
  const tranches = useMemo(() => withMarketOptions(INTAKE_TRANCHES, markets), [markets]);

  const t = INTAKE_STRINGS[lang];
  const toggleLang = useCallback(() => setLang((l) => (l === 'en' ? 'es' : 'en')), []);
  const tranche = tranches[trancheIdx];

  // ---- token check (anon RPC; returns no PII) ----------------------------
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      if (!token) {
        setPhase('invalid');
        return;
      }
      const { data, error } = await supabase.rpc('sourcing_intake_context', {
        p_token: token,
      });
      if (cancelled) return;
      const ctx = data as
        | { valid?: boolean; kind?: string; prefill?: Record<string, unknown>; markets?: unknown }
        | null;
      const valid = !error && Boolean(ctx?.valid);
      if (valid) setMarkets(marketsFromContext(ctx?.markets));
      if (valid && ctx?.kind === 'prospect') {
        setIsProspectLink(true);
        // The link carries who this is, so the form opens already filled in.
        const pre = prefillAnswers(ctx.prefill);
        if (Object.keys(pre).length > 0) setAnswers((a) => ({ ...pre, ...a }));
      }
      setPhase(valid ? 'intro' : 'invalid');
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const progress = useMemo(
    () => `${t.step} ${trancheIdx + 1} ${t.of} ${tranches.length}`,
    [t, trancheIdx, tranches.length],
  );

  // ---- submit: the whole application, always ------------------------------
  const submit = async () => {
    if (!token) return;
    setPhase('submitting');
    setErr(null);
    const contact = extractContact(answers);
    const signals = extractSignals(answers);
    const { error } = isProspectLink
      ? await supabase.rpc('submit_sourcing_intake_for_prospect', {
          p_token: token,
          p_language: lang,
          p_answers: answers,
          p_business_name: contact.business_name || null,
          p_owner_name: contact.owner_name,
          p_phone: contact.phone,
          p_email: contact.email,
          p_market: contact.market,
          p_signals: signals,
        })
      : await supabase.rpc('submit_sourcing_intake', {
          p_token: token,
          p_language: lang,
          p_answers: answers,
          p_business_name: contact.business_name || '',
          p_owner_name: contact.owner_name,
          p_phone: contact.phone,
          p_email: contact.email,
          p_market: contact.market,
          // The function still takes the flag; nothing can stop the form early
          // any more, so it is always false (the leftover field stays, TCSD-0493).
          p_early_exit: false,
          p_signals: signals,
        });
    if (error) {
      setErr(t.error);
      setPhase('form');
      return;
    }
    setPhase('done');
  };

  const advance = () => {
    setErr(null);
    const found = trancheProblems(tranche, answers);
    setProblems(found);
    const firstBad = tranche.questions.find((q) => found[q.id]);
    if (firstBad) {
      setErr(problemText(found[firstBad.id], t));
      // Put the person where the problem is rather than making them hunt.
      const el = document.getElementById(`q-${firstBad.id}`);
      if (el) {
        el.scrollIntoView({ block: 'center' });
        const field = el.querySelector('input, textarea');
        if (field instanceof HTMLElement) field.focus({ preventScroll: true });
      }
      return;
    }
    if (trancheIdx + 1 >= tranches.length) {
      void submit();
      return;
    }
    setTrancheIdx((i) => i + 1);
    scrollPageTop();
  };

  if (phase === 'loading') {
    return (
      <Shell t={t} onToggleLang={toggleLang}>
        <div className="flex justify-center py-16 text-tcs-text-muted">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      </Shell>
    );
  }

  if (phase === 'invalid') {
    return (
      <Shell t={t} onToggleLang={toggleLang}>
        <div className="rounded-xl border border-tcs-border bg-tcs-raised p-5 text-sm text-tcs-text-secondary">
          {t.invalid_link}
        </div>
      </Shell>
    );
  }

  if (phase === 'done') {
    return (
      <Shell t={t} onToggleLang={toggleLang}>
        <div className="rounded-xl border border-tcs-border bg-tcs-raised p-6 text-center">
          <CheckCircle2 className="mx-auto h-10 w-10 text-st-ok-fg" />
          <h2 className="mt-3 text-lg font-semibold text-tcs-text">
            {t.done_title}
          </h2>
          <p className="mt-1 text-sm text-tcs-text-secondary">
            {t.done_body}
          </p>
        </div>
      </Shell>
    );
  }

  if (phase === 'intro') {
    return (
      <Shell t={t} onToggleLang={toggleLang}>
        <div className="rounded-xl border border-tcs-border bg-tcs-raised p-5">
          <p className="text-sm leading-relaxed text-tcs-text-secondary">{t.intro}</p>
          <button
            type="button"
            onClick={() => setPhase('form')}
            className="mt-4 min-h-12 w-full rounded-lg bg-tcs-inverse px-4 text-base font-semibold text-tcs-inverse-ink"
          >
            {t.start}
          </button>
        </div>
      </Shell>
    );
  }

  // ---- form (one tranche at a time) ---------------------------------------
  return (
    <Shell t={t} onToggleLang={toggleLang}>
      <div className="rounded-xl border border-tcs-border bg-tcs-raised p-5">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-base font-semibold text-tcs-text">{tranche.title[lang]}</h2>
          <span className="text-[11px] font-medium text-tcs-text-muted">{progress}</span>
        </div>

        <div className="space-y-5">
          {tranche.questions.map((q) => (
            <QuestionField
              key={q.id}
              q={q}
              lang={lang}
              value={answers[q.id] ?? ''}
              problem={problems[q.id]}
              t={t}
              onChange={(v) => {
                setAnswers((a) => ({ ...a, [q.id]: v }));
                // The message goes the moment they start fixing it. A refusal
                // that stays on screen while the person corrects it reads as
                // the correction not counting.
                setProblems((pr) => {
                  if (!pr[q.id]) return pr;
                  const next = { ...pr };
                  delete next[q.id];
                  return next;
                });
              }}
            />
          ))}
        </div>

        {err && <p className="mt-3 text-sm text-st-danger-fg">{err}</p>}

        <div className="mt-5 flex gap-2">
          {trancheIdx > 0 && (
            <button
              type="button"
              onClick={() => setTrancheIdx((i) => i - 1)}
              className="min-h-12 rounded-lg border border-tcs-border-strong px-4 text-sm font-medium text-tcs-text-secondary"
            >
              {t.back}
            </button>
          )}
          <button
            type="button"
            onClick={advance}
            disabled={(phase as Phase) === 'submitting'}
            className="min-h-12 flex-1 rounded-lg bg-tcs-inverse px-4 text-base font-semibold text-tcs-inverse-ink disabled:opacity-60"
          >
            {(phase as Phase) === 'submitting'
              ? t.submitting
              : trancheIdx + 1 >= tranches.length
                ? t.submit
                : t.next}
          </button>
        </div>
      </div>
    </Shell>
  );
}

/** One line, in the applicant's language, saying what is wrong. */
function problemText(p: AnswerProblem, t: Record<string, string>): string {
  if (p === 'phone') return t.invalid_phone;
  if (p === 'email') return t.invalid_email;
  return t.required;
}

function QuestionField({
  q,
  lang,
  value,
  problem,
  t,
  onChange,
}: {
  q: IntakeQuestion;
  lang: Lang;
  value: string;
  problem?: AnswerProblem;
  t: Record<string, string>;
  onChange: (v: string) => void;
}) {
  return (
    <div id={`q-${q.id}`}>
      <label className="block text-sm font-medium text-tcs-text">
        {q.prompt[lang]}
        {q.required && <span className="text-st-danger-fg"> *</span>}
      </label>
      {q.help && <p className="mt-0.5 text-xs text-tcs-text-muted">{q.help[lang]}</p>}

      {q.type === 'long_text' ? (
        // Part two asks for a story, not a line. A single-line input on a phone
        // is what makes an applicant answer in four words.
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          rows={5}
          className={`mt-2 w-full rounded-lg border px-3 py-2 text-base ${
            problem ? 'border-st-danger-fg' : 'border-tcs-border-strong'
          }`}
          style={{ fontSize: 16 }} // prevents iOS zoom
        />
      ) : q.type === 'text' ? (
        <input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          inputMode={q.inputMode === 'tel' ? 'tel' : q.inputMode === 'email' ? 'email' : 'text'}
          className={`mt-2 min-h-12 w-full rounded-lg border px-3 text-base ${
            problem ? 'border-st-danger-fg' : 'border-tcs-border-strong'
          }`}
          style={{ fontSize: 16 }} // prevents iOS zoom
        />
      ) : (
        <div className="mt-2 space-y-2">
          {(q.options ?? []).map((o) => (
            <button
              key={o.value}
              type="button"
              onClick={() => onChange(o.value)}
              className={`min-h-12 w-full rounded-lg border px-3 py-2.5 text-left text-sm font-medium transition ${
                value === o.value
                  ? 'border-tcs-inverse bg-tcs-inverse text-tcs-inverse-ink'
                  : 'border-tcs-border-strong bg-tcs-raised text-tcs-text-secondary'
              }`}
            >
              {o.label[lang]}
            </button>
          ))}
        </div>
      )}

      {problem && (
        <p role="alert" className="mt-1.5 text-sm text-st-danger-fg">
          {problemText(problem, t)}
        </p>
      )}
    </div>
  );
}
